import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  Actions,
  closeDialog,
  Dropdown,
  DropdownDivider,
  DropdownItem,
  IconButton,
  openConfirmDialog,
  openDialog,
  showNotification,
  useDialog,
  DataStatus,
  Tooltip,
} from 'platform/components';
import {Hide, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {defaultTo, includes, isNil, not} from 'ramda';

import {EntityResourceIds} from '@dms/api/shared';
import {getApiErrorMessages} from '@dms/api/utils';
import {
  OrderActionKey,
  useGetMetadaServiceCaseQuery,
  useLazyGetServiceOrderVariantQuery,
  usePostOrderActionMutation,
  usePostServiceOrderBulkPrintDocumentsMutation,
} from '@dms/api/workshop';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  handleApiError,
  ORDER_TABS,
  printPDF,
  queryParams,
  workshopSearchParams,
  useFileNotification,
} from '@dms/shared';
import {
  SendAfterSalesEmailForm,
  SendAfterSalesCustomerSmsForm,
  useServiceCaseDetailActions,
} from '@dms/shared-aftersales';

import {buildArray, isNotNilOrEmpty, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EmailSmsNotificationBoundary} from '../../../../../components/EmailSmsNotifications/EmailSmsNotificationBoundary';
import {useRefreshDocumentCount} from '../../../../../hooks/useRefreshDocumentCount';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {ServiceCaseOrder} from '../../../../../types/OrderType';
import {ORDER_ACTION_PREFIX, ORDER_DECISION_ON_CLAIM_ACTION} from '../constants/actionKeys';
import {useHandleServiceOrderMandatoryFieldsError} from '../hooks/useHandleServiceOrderMandatoryFieldsError';
import {useOrderActions} from '../hooks/useOrderActions';
import {CancelOrderWithReasonDialog} from './CancelOrderWithReasonDialog';
import {ChangeOrderVariantForm} from './ChangeOrderVariantForm';
import {CreateSupplierOrdersDialog} from './CreateSupplierOrdersDialog/CreateSupplierOrdersDialog';
import {DecisionOnClaimModal} from './DecisionOnClaimModal';
import {GenerateDocumentsDialog} from './GenerateDocumentsDialog/GenerateDocumentsDialog';
import {IntegrationsDropdown} from './IntegrationsDropdown';
import {PackagesDropdown} from './PackagesDropdown';
import {SynchronizeCustomerConfirm} from './SynchronizeCustomerConfirm';

const CANCEL_ACTION_KEYS: OrderActionKey[] = ['cancel', 'cancel-claim'];
// Exposed as the "Ready to invoice" switch in the card header instead of a menu item or a button.
const LOCK_ACTION_KEYS: OrderActionKey[] = ['lock', 'unlock'];

const DROPDOWN_LIST_ITEM_TYPE = 'list-item';

const CREATE_SUPPLIER_ORDERS_DIALOG_ID = 'create-supplier-orders-dialog';

const ACTIONS_WITH_DEDICATED_BUTTONS = new Set([
  'order-list',
  'claim-handling-protocol',
  'claim-resolution-protocol',
  ...LOCK_ACTION_KEYS,
]);

interface OrderActionsProps extends RequiredTestIdProps {
  orderId: string;
  isExpanded: boolean;
  isOrderReadOnly: boolean;
  canCancelOrder: boolean;
  serviceOrder: ServiceCaseOrder;
  isSaveRequestPending: boolean;
  refreshServiceOrderDocumentDataGrid: () => void;
  onSaveRequestPendingChange: (isPending: boolean) => void;
  onExpand: () => void;
  onDataChange: () => void;
  onSaveRequest?: () => Promise<void>;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function OrderActions(props: OrderActionsProps) {
  const {serviceCaseId} = useWorkshopUrl();
  const [, setSearchParams] = useSearchParams();
  const [postOrderAction] = usePostOrderActionMutation();
  const [postServiceOrderBulkPrintDocuments] = usePostServiceOrderBulkPrintDocumentsMutation();
  const {executeAction, isAnyActionPending} = useServiceCaseDetailActions();
  const isAnyPending = isAnyActionPending || props.isSaveRequestPending;

  const [
    isSynchronizeCustomerDialogOpen,
    openSynchronizeCustomerDialog,
    closeSynchronizeCustomerDialog,
  ] = useDialog();

  const fileNotification = useFileNotification({timeoutMs: 30000});

  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId: String(serviceCaseId)}, {skip: !serviceCaseId});

  const {
    orderActions,
    isLoading: isOrderActionsLoading,
    isError: isOrderActionsError,
  } = useOrderActions({
    serviceCaseId,
    serviceOrderId: props.orderId,
    embeddedOrderActions: props.serviceOrder?.orderActions,
  });
  const [triggerServiceOrderVariant] = useLazyGetServiceOrderVariantQuery();

  const handleMandatoryFieldsError = useHandleServiceOrderMandatoryFieldsError({
    orderId: props.orderId ?? '',
    serviceOrderVariantId:
      props.serviceOrder?.serviceOrderVariant?.serviceOrderVariantId ?? undefined,
    vehicleId: serviceCase?.vehicleId ?? '',
  });

  const refreshDocumentCount = useRefreshDocumentCount(defaultTo('', serviceCaseId));

  const handleOrderCancel = async (actionKey: 'cancel' | 'cancel-claim') => {
    const serviceOrderVariantId = props.serviceOrder?.serviceOrderVariant?.serviceOrderVariantId;

    if (isNotNilOrEmpty(serviceOrderVariantId)) {
      const serviceOrderVariant = await triggerServiceOrderVariant({serviceOrderVariantId}, true)
        .unwrap()
        .catch((error: FetchBaseQueryError) => {
          handleApiError(error);
          return null;
        });

      if (isNil(serviceOrderVariant)) {
        return;
      }

      if (serviceOrderVariant?.isCancelReasonRequired) {
        return openDialog(
          <CancelOrderWithReasonDialog
            serviceOrderId={props.orderId ?? ''}
            serviceCaseId={serviceCaseId ?? ''}
            actionKey={actionKey}
            onClose={() => closeDialog('cancelOrderDialog')}
            data-testid={suffixTestId('cancelOrderDialog', props)}
          />,
          {
            id: 'cancelOrderDialog',
            size: 'small',
            title: i18n.t('entity.order.actions.cancel'),
          }
        );
      }
    }

    openConfirmDialog({
      text: i18n.t('entity.order.labels.cancel', {name: props.serviceOrder?.number}),
      'data-testid': suffixTestId('cancel', props),
      onConfirm: () =>
        executeAction(`${ORDER_ACTION_PREFIX}${actionKey}`, () =>
          postOrderAction({
            serviceCaseId: String(serviceCaseId),
            serviceOrderId: String(props.orderId),
            actionKey,
          })
            .unwrap()
            .then(() =>
              showNotification.success(i18n.t('entity.order.notifications.success-cancel'))
            )
            .catch(handleApiError)
        ),
    });
  };

  const handleOpenOrderVariantDialog = () => {
    if (
      isNil(props.orderId) ||
      isNil(serviceCaseId) ||
      isNil(props.serviceOrder?.serviceOrderVariant?.serviceOrderVariantId)
    ) {
      throw new Error('The dialog cannot be opened due to missing data');
    }

    openDialog(
      <ChangeOrderVariantForm
        orderId={props.orderId ?? ''}
        serviceCaseId={serviceCaseId ?? ''}
        orderVariant={props.serviceOrder?.serviceOrderVariant?.serviceOrderVariantId ?? ''}
        onSubmitted={props.onDataChange}
        onClose={() => closeDialog('changeOrderVariantDialog')}
        data-testid={suffixTestId('chnageOrderVariant', props)}
      />,
      {
        id: 'changeOrderVariantDialog',
        size: 'small',
        title: i18n.t('entity.order.actions.changeOrder'),
      }
    );
  };

  const handleOpenCreateSupplierOrdersDialog = () =>
    openDialog(
      <CreateSupplierOrdersDialog
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.orderId}
        onClose={() => closeDialog(CREATE_SUPPLIER_ORDERS_DIALOG_ID)}
        data-testid={suffixTestId('createSupplierOrders', props)}
      />,
      {
        id: CREATE_SUPPLIER_ORDERS_DIALOG_ID,
        title: i18n.t('entity.order.actions.create-supplier-order'),
        scrollBehavior: 'outside',
      }
    );

  const handleSendSms = () => {
    if (!props.orderId || !serviceCaseId || !serviceCase?.customerId) {
      return;
    }

    openDialog(
      <SendAfterSalesCustomerSmsForm
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.orderId}
        recordId={props.orderId}
        resourceId={EntityResourceIds.serviceOrder}
      />,
      {
        title: i18n.t('general.actions.sendSms'),
        size: 'large',
        scrollBehavior: 'outside',
      }
    );
  };

  const handleSendEmail = () => {
    if (!props.orderId || !serviceCaseId) {
      return;
    }

    openDialog(
      <SendAfterSalesEmailForm
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.orderId}
        defaultValues={{defaultCategory: 'customer-message'}}
      />,
      {
        id: 'email-sms-notification-dialog',
        withAdditionalFooter: true,
        size: 'large',
        title: i18n.t('general.actions.sendEmail'),
      }
    );
  };

  const handleSubmitDecisionOnClaim = () =>
    executeAction(ORDER_DECISION_ON_CLAIM_ACTION, () =>
      postServiceOrderBulkPrintDocuments({
        serviceCaseId: String(serviceCaseId),
        serviceOrderId: String(props.orderId),
        body: [{key: 'claim-resolution-protocol'}],
      }).unwrap()
    )
      .then((documents) => {
        const document = documents?.find(
          (item) => item.documentKey === 'claim-resolution-protocol'
        );
        const fileId = document?.printout?.fileId;

        if (fileId) {
          fileNotification.registerFile(fileId, {
            onSuccess: () => {
              if (document?.printout?.pdfUrl) {
                printPDF(document.printout.pdfUrl);
              } else {
                showNotification.error();
              }
            },
          });
        } else {
          showNotification.error();
        }
      })
      .then(() => closeDialog('decision-on-claim-dialog'))
      .then(() =>
        showNotification.success(
          i18n.t('entity.order.notifications.success-claim-resolution-protocol')
        )
      )
      .then(() => {
        refreshDocumentCount();
        props.refreshServiceOrderDocumentDataGrid();
      })
      .catch((error: FetchBaseQueryError) =>
        handleMandatoryFieldsError(error, 'BEFORE_OPENING', {
          silent: true,
          callback: () => showNotification.error(getApiErrorMessages(error)),
        })
      );

  const handleOpenGenerateDocumentsDialog = () => {
    if (!serviceCaseId || !props.orderId) {
      return;
    }

    const openGenerateDialog = () =>
      openDialog(
        <GenerateDocumentsDialog
          serviceCaseId={serviceCaseId}
          serviceOrderId={props.orderId}
          onClose={() => closeDialog('generate-documents-dialog')}
          refreshDocuments={() => {
            refreshDocumentCount();
            props.refreshServiceOrderDocumentDataGrid();
          }}
          data-testid={suffixTestId('generateDocuments', props)}
        />,
        {
          id: 'generate-documents-dialog',
          title: i18n.t('entity.order.actions.generate-documents'),
          scrollBehavior: 'outside',
        }
      );

    if (props.onSaveRequest) {
      props.onSaveRequestPendingChange(true);
      props
        .onSaveRequest()
        .then(openGenerateDialog)
        .catch(handleApiError)
        .finally(() => props.onSaveRequestPendingChange(false));
    } else {
      openGenerateDialog();
    }
  };

  const handleOrderActionClick = (actionKey: OrderActionKey | Nullish) => {
    match(actionKey)
      .with('change-order', handleOpenOrderVariantDialog)
      .with('create-supplier-order', handleOpenCreateSupplierOrdersDialog)
      .with('cancel', 'cancel-claim', handleOrderCancel)
      .with('payment', () =>
        setSearchParams({
          [queryParams.COMPONENT_SECTIONS_SECTION]: workshopSearchParams.serviceDetail.orders,
          [queryParams.SERVICE_CASE_ORDER_TAB]: String(ORDER_TABS.CHECKOUT),
          [queryParams.SERVICE_CASE_ORDER_ID]: props.orderId ?? '',
        })
      )
      .with('send-email-sms', () => {
        if (!props.orderId || !serviceCaseId) {
          return;
        }

        openDialog(
          <EmailSmsNotificationBoundary
            orderId={props.orderId}
            serviceCaseId={serviceCaseId}
            dialogId="email-sms-notification-dialog"
            serviceOrder={props.serviceOrder}
            data-testid={suffixTestId('emailSmsNotification', props)}
          />,
          {
            id: 'email-sms-notification-dialog',
            size: 'large',
            title: i18n.t('entity.order.actions.sendEmailSms'),
          }
        );
      })
      .with('decision-on-claim', () => {
        if (!serviceCaseId || !props.orderId) {
          return;
        }

        openDialog(
          <DecisionOnClaimModal
            serviceCaseId={serviceCaseId}
            serviceOrderId={props.orderId}
            onClose={() => closeDialog('decision-on-claim-dialog')}
            onSubmit={handleSubmitDecisionOnClaim}
            data-testid={suffixTestId('decisionOnClaim', props)}
          />,
          {
            id: 'decision-on-claim-dialog',
            title: i18n.t('entity.order.labels.decisionOnClaim'),
          }
        );
      })
      .with('synchronize-customer', () => {
        openSynchronizeCustomerDialog();
      })
      .otherwise((actionKey) => {
        if (isNil(actionKey)) {
          throw new Error(`Action callback was not specified for action ${actionKey}`);
        }

        executeAction(`${ORDER_ACTION_PREFIX}${actionKey}`, () =>
          postOrderAction({
            serviceCaseId: String(serviceCaseId),
            serviceOrderId: String(props.orderId),
            actionKey,
          }).unwrap()
        )
          .then(() => {
            refreshDocumentCount();
            props.refreshServiceOrderDocumentDataGrid();

            showNotification.success(i18n.t(`entity.order.notifications.success-${actionKey}`), {
              'data-testid': suffixTestId('successNotification', props),
            });
          })
          .catch((error: FetchBaseQueryError) => {
            handleMandatoryFieldsError(error, 'BEFORE_OPENING', {
              silent: true,
              skipQueryStateUpdate: true,
              callback: () =>
                showNotification.error(
                  getApiErrorMessages(
                    error,
                    i18n.t(`entity.order.notifications.error-${actionKey}`)
                  )
                ),
            });
          });
      });
  };

  const isOrderActive = not(includes(props.serviceOrder?.state, ['ARCHIVED', 'CANCELLED']));

  const isActionCancelDenied = (actionKey: OrderActionKey | Nullish) =>
    includes(actionKey, CANCEL_ACTION_KEYS) && !props.canCancelOrder;
  const isOptionClickCancelDenied = isActionCancelDenied(orderActions?.optionClick);

  const isActionSendEmailSms =
    orderActions?.optionClick === 'send-email-sms' &&
    isFeatureEnabled(featureFlags.MESSAGE_EMAIL_REDESIGN_AFTERSALES);

  const cardActions = buildArray<Action>()
    .when(
      isActionSendEmailSms,
      {
        type: 'button',
        title: i18n.t(`general.actions.sendSms`),
        variant: 'outlined',
        isDisabled:
          orderActions?.options?.find((option) => option?.key === orderActions?.optionClick)
            ?.style === 'disabled' || isAnyPending,
        onClick: (event) => {
          event.stopPropagation();
          handleSendSms();
        },
      },
      {
        type: 'button',
        title: i18n.t(`general.actions.sendEmail`),
        variant: 'outlined',
        isDisabled:
          orderActions?.options?.find((option) => option?.key === orderActions?.optionClick)
            ?.style === 'disabled' || isAnyPending,
        onClick: (event) => {
          event.stopPropagation();
          handleSendEmail();
        },
      }
    )
    .when(
      !!orderActions?.optionClick &&
        !isActionSendEmailSms &&
        !ACTIONS_WITH_DEDICATED_BUTTONS.has(orderActions?.optionClick),
      {
        type: 'button',
        title: i18n.t(`entity.order.actions.${orderActions?.optionClick}`),
        variant: orderActions?.optionClick === 're-open' ? 'primary' : 'outlined',
        isDisabled:
          orderActions?.options?.find((option) => option?.key === orderActions?.optionClick)
            ?.style === 'disabled' ||
          isAnyPending ||
          isOptionClickCancelDenied,
        // Only the permission gate earns this label - a backend-disabled action is not a permission problem
        tooltip: isOptionClickCancelDenied ? i18n.t('general.labels.noPermission') : undefined,
        onClick: (event) => {
          event.stopPropagation();
          handleOrderActionClick(orderActions?.optionClick);
        },
      }
    )
    .when(isOrderActive, {
      type: 'button',
      title: i18n.t('entity.order.actions.generate-documents'),
      variant: 'outlined',
      isDisabled: isAnyPending,
      isLoading: props.isSaveRequestPending,
      onClick: (event) => {
        event.stopPropagation();
        handleOpenGenerateDocumentsDialog();
      },
    })
    .add({
      type: 'iconButton',
      priority: 'tertiary',
      icon: props.isExpanded ? 'navigation/expand_less' : 'navigation/expand_more',
      onClick: (event) => {
        event.stopPropagation();
        props.onExpand();
      },
    });

  const dropdownOptions = orderActions?.options?.filter((option) =>
    not(includes(option?.key, LOCK_ACTION_KEYS))
  );

  // Dividers and empty entries must not open a menu on their own.
  const isActionsDropdownVisible =
    !!dropdownOptions?.some(
      (option) => option?.type === DROPDOWN_LIST_ITEM_TYPE && option?.style !== 'hidden'
    ) && isOrderActive;

  const isLoading = isOrderActionsLoading || isServiceCaseLoading;
  const isError = isOrderActionsError || isServiceCaseError;

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <HStack spacing={2}>
        <Show when={isActionsDropdownVisible}>
          <div onClick={(event) => event.stopPropagation()}>
            <Dropdown
              placement="bottom-end"
              dropdownControl={
                <IconButton
                  size="small"
                  icon="navigation/more_vert"
                  priority="tertiary"
                  aria-label={i18n.t('general.labels.more')}
                  data-testid={suffixTestId('small', props)}
                />
              }
              onButtonClick={(event) => event.stopPropagation()}
              data-testid={suffixTestId('actionsDropdown', props)}
            >
              {dropdownOptions?.map((option) => {
                const isCancelDenied = isActionCancelDenied(option?.key);

                return (
                  <Hide when={option?.style === 'hidden'} key={option?.position}>
                    <Show when={option?.type === 'divider'}>
                      <DropdownDivider />
                    </Show>
                    <Show when={option?.type === DROPDOWN_LIST_ITEM_TYPE}>
                      <Tooltip
                        isDisabled={!isCancelDenied}
                        label={i18n.t('general.labels.noPermission')}
                      >
                        <DropdownItem
                          label={i18n.t(`entity.order.actions.${option?.key}`)}
                          isDisabled={
                            option?.style === 'disabled' || isAnyPending || isCancelDenied
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOrderActionClick(option?.key);
                          }}
                        />
                      </Tooltip>
                    </Show>
                  </Hide>
                );
              })}
            </Dropdown>
          </div>
        </Show>
        <Hide when={props.isOrderReadOnly}>
          <IntegrationsDropdown
            serviceCaseId={defaultTo('', serviceCaseId)}
            serviceOrderId={defaultTo('', props.orderId)}
            isExternalActionPending={isAnyPending}
            isOrderReadOnly={props.isOrderReadOnly}
          />
        </Hide>
        <Hide when={props.isOrderReadOnly}>
          <PackagesDropdown
            serviceCaseId={defaultTo('', serviceCaseId)}
            serviceOrderId={defaultTo('', props.orderId)}
            isDisabled={isAnyPending}
            data-testid={suffixTestId('packages', props)}
          />
        </Hide>
        <Actions size="small" data-testid={suffixTestId('actions', props)} actions={cardActions} />
        <SynchronizeCustomerConfirm
          dialogId="synchronize-customer-dialog"
          isOpen={isSynchronizeCustomerDialogOpen}
          onComplete={closeSynchronizeCustomerDialog}
          serviceCaseId={serviceCaseId}
          serviceOrderId={props.orderId}
          data-testid={suffixTestId('synchronizeCustomer', props)}
        />
      </HStack>
    </DataStatus>
  );
}
